<template>
  <div class="my-10 flex flex-col">
    <CommentModal :item-id="currentId" origin="product"></CommentModal>
    <ReportModal :item-id="currentId" origin="product"></ReportModal>
    <RateModal @rated="setUserRate" :item-id="currentId" origin="product"></RateModal>
    <div class="grid grid-cols-2 gap-5 max-lg:grid-cols-1">
      <div class="flex flex-col">
        <div>
          <video
            controlsList="nodownload"
            controls
            v-if="isVideo(currentImage)"
            :src="currentImage"
            class="w-full h-[25rem] object-contain rounded-lg cursor-pointer"
          ></video>
          <template v-else>
            <img
              v-if="currentImage"
              @click="onShow"
              :src="currentImage"
              alt="main-image"
              class="w-full h-[25rem] object-fill rounded-lg cursor-pointer"
            />
            <vue-easy-lightbox
              :visible="visibleRef"
              :imgs="currentImage"
              :index="indexRef"
              @hide="onHide"
            ></vue-easy-lightbox>
          </template>
        </div>
        <div>
          <Splide class="" :has-track="false" :options="imageOptions" aria-label="carouselBanner">
            <SplideTrack class="rounded-lg py-2">
              <SplideSlide class="" v-for="(item, index) in detail.images" :key="item.id">
                <video
                  controlsList="nodownload"
                  v-if="isVideo(item.path)"
                  @click="changeImage(index)"
                  :src="item.path"
                  class="h-32 w-full object-cover rounded-md cursor-pointer"
                ></video>
                <img
                  v-else
                  @click="changeImage(index)"
                  :src="item.path"
                  class="h-32 w-full object-fill rounded-md cursor-pointer"
                  alt=""
                />
              </SplideSlide>
            </SplideTrack>
          </Splide>
        </div>
        <div v-if="advertisements.length > 0">
          <DetailBanner :content="advertisements"></DetailBanner>
        </div>
        <router-link :to="{ name: 'storeDetail', params: { id: detail?.store?.id } }">
          <div class="mt-5 bg-white p-5 rounded-md">
            <div>
              <p class="text-main-color font-bold text-xl">{{ $t('page.seller') }}</p>
            </div>
            <div class="flex gap-5 mt-4 min-w-0">
              <div class="shrink-0">
                <img
                  :src="detail?.store?.thumbnail"
                  class="h-36 w-36 rounded-md object-cover"
                  alt=""
                />
              </div>

              <div class="min-w-0 flex-1">
                <p class="mt-1 text-lg font-bold truncate" :title="detail?.store?.name">
                  {{ detail?.store?.name }}
                </p>

                <p class="mt-1 truncate" :title="detail?.store?.location">
                  {{ $t('page.location') }}:
                  <span class="font-semibold">{{ detail?.store?.location }}</span>
                </p>

                <p v-if="sellerFirstPhone" class="mt-1 truncate" :title="sellerFirstPhone">
                  <span class="font-semibold">{{ sellerFirstPhone }}</span>
                </p>

                <p
                  v-if="detail?.store?.caption"
                  class="mt-1 text-[#333333] font-light truncate"
                  :title="detail?.store?.caption"
                >
                  {{ detail?.store?.caption }}
                </p>
              </div>
            </div>
          </div>
        </router-link>
      </div>
      <div>
        <div>
          <div class="border-b border-[#E3E3E3] mb-4">
            <div class="flex gap-3 mb-3">
              <span
                v-if="detail?.is_available && productIsNew"
                class="m-1 bg-green-500 text-white text-sm font-semibold rounded-lg p-1"
                >{{ $t('page.new') }}</span
              >
              <span
                v-if="detail?.is_available && productInPromotion"
                class="m-1 bg-red-500 text-white text-sm w-7 h-7 text-center font-semibold rounded-full p-1"
                >A</span
              >
              <span
                v-if="detail?.is_available && productDiscountPercent"
                class="m-1 font-semibold bg-red-500 text-white text-sm text-center rounded-lg p-1"
                >-{{ productDiscountPercent }}%</span
              >
            </div>

            <div class="flex justify-between items-center gap-3 min-w-0">
              <p
                class="font-semibold text-main-color text-3xl mb-3 min-w-0 flex-1 truncate"
                :title="detail?.name"
              >
                {{ detail.name }}
              </p>

              <button
                :disabled="loading"
                class="rounded-full bg-[#409DBA] h-[2.1875rem] w-[2.1875rem] flex justify-center items-center cursor-pointer shrink-0"
                @click="likeAction"
              >
                <IconHeart :class="{ 'fill-white': this.isLiked }"></IconHeart>
              </button>
            </div>

            <div class="flex items-center justify-between gap-5 mb-6">
              <div class="flex gap-5 items-center">
                <p v-if="productDiscount == null" class="text-[#409DBA] font-bold text-2xl">
                  {{ formatNumber(productPrice) }} TMT
                </p>
                <div class="">
                  <p class="text-[#409DBA] font-bold text-2xl" v-if="productDiscountPercent">
                    {{ formatNumber(productPrice - productPrice * (productDiscountPercent / 100)) }}
                    TMT
                    <span class="line-through font-semibold text-lg text-[#CB3131]"
                      >{{ formatNumber(productPrice) }} TMT</span
                    >
                  </p>
                </div>
                <div class="flex items-center gap-1">
                  <IconEye></IconEye>
                  <p class="text-main-color font-semibold text-sm">{{ detail.view_count }}</p>
                </div>
              </div>
              <VueStarRating
                :rounded-corners="true"
                :star-size="15"
                active-color="#ffb300"
                inactive-color="#f2f2f2"
                :border-width="2"
                border-color="#ffb300"
                :show-rating="false"
                :read-only="true"
                :increment="0.5"
                :rating="detail?.rating"
              ></VueStarRating>
            </div>
            <div v-if="!productIsAvailable" class="mb-3">
              <p class="text-red-500 font-semibold text-lg">{{ $t('page.outOfStock') }}</p>
            </div>
            <div v-if="detail?.is_available" class="mb-3 flex font-semibold items-center gap-2">
              {{ $t('page.onCredit') }} <IconChecked v-if="productForCredit" />
              <IconUnchecked v-else />
            </div>
            <div
              v-if="detail?.is_available && productInPromotion"
              class="mb-3 flex font-semibold text-xl items-center gap-2 text-red-500"
            >
              {{ $t('page.promotion') }}
            </div>
            <div class="mb-3 flex items-center gap-2">
              <p>{{ $t('page.yourRate') }}:</p>
              <VueStarRating
                :rounded-corners="true"
                :star-size="15"
                active-color="#ffb300"
                inactive-color="#f2f2f2"
                :border-width="2"
                border-color="#ffb300"
                :show-rating="false"
                :increment="1"
                :read-only="true"
                :rating="userRating"
              ></VueStarRating>
            </div>
          </div>

          <div class="w-full">
            <div v-if="detail?.stocks?.length > 0" class="border-b border-[#E0E0E0] pb-5">
              <p class="text-main-color text-lg font-bold mb-3 mt-5">{{ $t('page.selectSize') }}</p>
              <div class="flex gap-3">
                <div
                  v-for="(stock, index) in detail.stocks"
                  :key="index"
                  @click="selectStock(stock)"
                  :class="[
                    'bg-white rounded cursor-pointer text-center w-14',
                    { 'text-white bg-main-color rounded': selectedStock.size.id === stock.size.id },
                  ]"
                >
                  <p
                    :class="[
                      'font-bold text-base p-3 text-main-color',
                      {
                        'text-white bg-main-color rounded': selectedStock.size.id === stock.size.id,
                      },
                    ]"
                  >
                    {{ stock.size.name }}
                  </p>
                </div>
              </div>
            </div>
            <div class="">
              <template v-if="detail?.store?.can_online_order">
                <div v-if="productIsAvailable" class="w-full mb-3">
                  <SubmitBtn
                    v-if="cartItem === null"
                    @onclick="addToCart"
                    :loading="loading"
                    class="!w-full block text-center rounded-sm bg-main-color text-base py-4 text-white font-semibold"
                  >
                    {{ $t('page.addToCart') }}</SubmitBtn
                  >
                  <div
                    v-else
                    class="flex items-center gap-5 mt-10 max-md:mt-6 max-md:justify-between"
                  >
                    <button
                      @click="updateCartItem(cartItem.quantity - 1)"
                      class="rounded-lg inline-flex justify-center items-center h-12.5 w-12.5 bg-main-color ease-linear transition-all duration-150 hover:bg-accent-color max-md:w-20 max-md:h-11"
                    >
                      <IconMinus
                        svgClass="h-6 w-6 max-md:h-5 max-md:w-5"
                        pathClass="stroke-white"
                      />
                    </button>
                    <input
                      class="w-16 h-12 text-center text-xl rounded border max-md:w-14"
                      readonly
                      type="text"
                      :value="cartItem.quantity"
                    />
                    <button
                      @click="updateCartItem(cartItem.quantity + 1)"
                      class="rounded-lg inline-flex justify-center items-center h-12.5 w-12.5 bg-main-color ease-linear transition-all duration-150 hover:bg-accent-color max-md:w-20 max-md:h-11"
                    >
                      <IconPlusRegular
                        svgClass="h-6 w-6 max-md:h-5 max-md:w-5"
                        pathClass="stroke-white"
                      />
                    </button>
                  </div>
                </div>
              </template>

              <div class="flex gap-3 justify-between">
                <CommentButton v-if="detail?.allow_comments"></CommentButton>
                <RateButton></RateButton>
                <ReportButton></ReportButton>
              </div>
            </div>

            <div class="mt-5 bg-white divide-y rounded mb-5">
              <p class="text-main-color text-base font-semibold p-4">{{ $t('page.detail') }}</p>
              <div class="p-4 flex flex-col gap-4">
                <div class="w-full flex items-center justify-between">
                  <p class="text-main-color font-semibold text-base">
                    {{ $t('page.productCode') }}:
                  </p>
                  <p class="text-main-color">{{ detail?.code }}</p>
                </div>
                <!-- <div class="w-full flex items-center justify-between">
                                    <p class="text-main-color font-semibold text-base">{{ $t('page.location') }}:</p>
                                    <p class="text-main-color">{{ detail?.store?.location }}</p>
                                </div> -->
                <div class="w-full flex items-center justify-between">
                  <p class="text-main-color font-semibold text-base">
                    {{ $t('page.manufacturedCountry') }}:
                  </p>
                  <p class="text-main-color">{{ detail?.country }}</p>
                </div>
                <div class="w-full flex items-center justify-between">
                  <p class="text-main-color font-semibold text-base">{{ $t('page.brand') }}:</p>
                  <router-link
                    :to="{
                      name: 'brandProducts',
                      params: { id: detail?.brand_id },
                      query: { brandName: detail?.brand },
                    }"
                    ><p class="text-accent-color font-bold">{{ detail?.brand }}</p></router-link
                  >
                </div>
              </div>
            </div>

            <div class="flex gap-1 mb-5">
              <IconComment></IconComment>
              <p>{{ detail.comments__count }} {{ $t('page.comments') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col w-full gap-5 border-b border-[#E0E0E0] my-10 pb-10">
      <p class="text-2xl font-bold text-main-color mb-2 text-center">{{ $t('page.info') }}</p>
      <div>
        <p class="font-light text-base text-[#333]">{{ detail?.description }}</p>
      </div>
    </div>
    <div v-if="similarProducts.length > 0">
      <p class="font-semibold text-main-color text-3xl mb-5">{{ $t('page.similarProducts') }}</p>

      <div class="flex overflow-hidden gap-5">
        <StoreProductCard
          v-for="(product, index) in similarProducts"
          :key="index"
          :detail="product"
        />
      </div>
    </div>
    <div v-if="comments.length">
      <p class="font-bold text-main-color text-3xl mt-20 mb-5 capitalize">
        {{ $t('page.comments') }}
      </p>

      <!-- TESWIRLER -->
      <vue-easy-lightbox
        :visible="visibleAttachmentRef"
        :imgs="attachmentImgsRef"
        :index="0"
        @hide="visibleAttachmentRef = false"
      ></vue-easy-lightbox>

      <div
        class="border-b border-[#E0E0E0] pb-5 mb-3"
        v-for="(comment, index) in comments"
        :key="index"
      >
        <div class="flex flex-col mb-4">
          <div class="flex flex-col">
            <div class="flex flex-col mb-2">
              <div class="flex flex-col mb-3">
                <p class="text-lg text-main-color font-bold">{{ comment.username }}</p>
                <p class="test-[#333333] font-light text-sm">
                  {{ formatDate(comment.date_created) }}
                </p>
              </div>
              <p class="font-light text-lg text-[#333]">{{ comment.text }}</p>
            </div>
            <!-- <div class="flex items-center mb-1 md:col-start-3">
                            <button class="bg-[#717171] text-white font-light text-xs py-2 px-[1.375rem] rounded"
                                @click="openReplyCommentModal(comment)">Jogap
                                ýaz</button>
                        </div> -->

            <div class="flex gap-5 items-center overflow-x-scroll mb-2">
              <div v-for="attachment in comment.attachments" :key="attachment.id">
                <img
                  @click="showAttachment(attachment.path)"
                  :src="attachment.path"
                  alt="Attachment Image"
                  class="max-w-[156px] max-h-[90px]"
                />
              </div>
            </div>
            <div class="flex gap-4 justify-end mb-2">
              <button class="flex gap-1 text-gray-600" @click="postCommentVote(comment.id, 1)">
                <IconLike :class="{ 'fill-[#a5ffab]': comment?.user_vote == 1 }" />
                {{ comment.up_vote_count }}
              </button>
              <button class="flex gap-1 text-gray-600" @click="postCommentVote(comment.id, -1)">
                <IconDislike :class="{ 'fill-[#ffb8b8]': comment?.user_vote == -1 }" />
                {{ comment.down_vote_count }}
              </button>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="reply in comment.replies" :key="reply.id" class="bg-[#e3e3e3] p-4 rounded">
              <div class="flex flex-col border-l border-solid border-main-color pl-3">
                <div class="flex items-center gap-4">
                  <p class="font-bold">{{ $t('page.owner') }}</p>
                  <p class="text-sm">{{ reply.date_created }}</p>
                </div>

                <div class="mt-2">
                  <p>{{ reply.text }}</p>
                </div>

                <div
                  v-if="reply.attachments && reply.attachments.length"
                  class="flex gap-5 items-center overflow-x-scroll mt-3"
                >
                  <div
                    v-for="attachment in reply.attachments"
                    :key="`reply-attachment-${attachment.id}`"
                  >
                    <img
                      @click="showAttachment(attachment.path)"
                      :src="attachment.path"
                      alt="Reply Attachment Image"
                      class="max-w-[156px] max-h-[90px] cursor-pointer"
                    />
                  </div>
                </div>
              </div>
              <div class="flex gap-4 justify-end">
                <button class="flex gap-1 text-gray-600" @click="postCommentVote(reply.id, 1)">
                  <IconLike :class="{ 'fill-[#a5ffab]': reply?.user_vote == 1 }" />
                  {{ reply.up_vote_count }}
                </button>
                <button class="flex gap-1 text-gray-600" @click="postCommentVote(reply.id, -1)">
                  <IconDislike :class="{ 'fill-[#ffb8b8]': reply?.user_vote == -1 }" />
                  {{ reply.down_vote_count }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- REPLIES -->
        <!-- <div v-for="(commentReply, index) in comment.replies" :key="index" class="ml-[5%]">
                    <div class="flex flex-col mb-4">
                        <div class="flex flex-col">
                            <p class="text-lg text-main-color font-bold">{{ commentReply.username }}</p>
                        </div>
                        <p class="font-light text-base text-[#333]">{{ commentReply.text }}</p>
                    </div>

                    <div class="flex gap-5 items-center overflow-x-scroll">
                        <div v-for="attachment in commentReply.attachments" :key="attachment.id">
                            <img :src="attachment.path" alt="Attachment Image" class="max-w-[156px] max-h-[90px]">
                        </div>
                    </div>
                </div> -->
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import { i18n } from '@/i18n';
import { mapState } from 'pinia';
import { useAppStore } from '@/store';
import VueEasyLightbox from 'vue-easy-lightbox';
import VueStarRating from 'vue-star-rating';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { Field, Form, ErrorMessage } from 'vee-validate';
import DetailBanner from '@cmp/DetailBanner.vue';
import StoreProductCard from '@cmp/mall/StoreProductCard.vue';
import IconPlus from '@icons/IconPlus.vue';
import IconPlusRegular from '@icons/IconPlusRegular.vue';
import IconMinus from '@icons/IconMinus.vue';
import IconHeart from '@icons/IconHeart.vue';
import IconEye from '@icons/IconEye.vue';
import IconClip from '@icons/IconClip.vue';
import IconComment from '@icons/IconComment.vue';
import IconChecked from '@icons/IconChecked.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import IconLike from '@icons/IconLike.vue';
import IconDislike from '@icons/IconDislike.vue';
import { formatDate, formatNumber, isVideo } from '@/helpers';
import Modal from '@cmp/Modal.vue';
import ReportButton from '@cmp/modals/buttons/ReportButton.vue';
import CommentButton from '@cmp/modals/buttons/CommentButton.vue';
import RateButton from '@cmp/modals/buttons/RateButton.vue';
import ReportModal from '@cmp/modals/ReportModal.vue';
import CommentModal from '@cmp/modals/CommentModal.vue';
import RateModal from '@cmp/modals/RateModal.vue';
export default {
  name: 'storedetailDetail',

  components: {
    VueEasyLightbox,
    VueStarRating,
    Splide,
    SplideSlide,
    SplideTrack,
    Field,
    Form,
    ErrorMessage,
    DetailBanner,
    StoreProductCard,
    IconPlus,
    IconPlusRegular,
    IconMinus,
    IconHeart,
    IconEye,
    IconClip,
    IconComment,
    IconChecked,
    IconUnchecked,
    Modal,
    IconLike,
    IconDislike,
    ReportModal,
    CommentModal,
    RateModal,
    ReportButton,
    CommentButton,
    RateButton,
  },
  data() {
    return {
      loading: false,
      currentId: null,
      detail: {},
      comments: [],
      advertisements: [],
      visibleRef: false,
      visibleAttachmentRef: false,
      indexRef: 0,
      triggerComputed: 0,
      imgsRef: [],
      attachmentImgsRef: [],
      currentImage: null,
      rateModal: false,
      similarProducts: [],
      variableProduct: false,
      productPrice: 0,
      productIsNew: false,
      productIsAvailable: false,
      productForCredit: false,
      productInPromotion: false,
      productDiscount: null,
      productDiscountPercent: null,
      selectedStock: null,
      isLiked: false,
      userRating: 0,
      formRate: {
        rating: 0,
      },
      imageOptions: {
        perPage: 3,
        perMove: 1,
        gap: '10px',
      },
    };
  },
  watch: {
    '$route.params.id': {
      async handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.currentId = newValue;
          await this.getProductDetail();
          await this.getProductAd();
          await this.getProductSimilars();
          await this.getProductComments();
        }
      },
    },
  },
  methods: {
    formatDate,
    formatNumber,
    isVideo,
    onShow() {
      if (!this.currentImage) {
        return;
      }

      this.visibleRef = true;
      this.imgsRef = [this.currentImage];
    },
    onAttachmentHide() {},
    onHide() {
      this.visibleRef = false;
    },
    showAttachment(img) {
      this.visibleAttachmentRef = true;
      this.attachmentImgsRef = [img];
    },

    changeImage(index) {
      const image = this.detail?.images?.[index];

      if (!image?.path) {
        return;
      }

      this.currentImage = image.path;
    },
    selectStock(stock) {
      this.selectedStock = stock;
      this.productPrice = stock.price;
      ((this.productIsNew = stock.is_new),
        (this.productIsAvailable = stock.is_available),
        (this.productForCredit = stock.for_credit),
        (this.productInPromotion = stock.in_promotion),
        (this.productDiscount = stock.discount),
        (this.productDiscountPercent = stock.discount_percent),
        this.triggerComputed++);
    },

    openRateModal() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      this.rateModal = true;
    },
    closeRateModal() {
      this.rateModal = false;
      this.formRate = {
        rating: 0,
      };
    },
    async likeAction() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        this.loading = true;
        const response = await api.post(`mall/products/${this.currentId}/like/`, {
          is_liked: !this.isLiked,
        });
        this.isLiked = !this.isLiked;
        this.$store.getLiked('mall');
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    getCurrentLocationId() {
      try {
        const savedLocation = JSON.parse(localStorage.getItem('selectedLocation')) || 1;

        if (typeof savedLocation === 'object') {
          return savedLocation?.id;
        }

        return savedLocation;
      } catch (e) {
        return 1;
      }
    },

    getProductAdLocationId() {
      return (
        this.detail?.store?.location_root_id ||
        this.detail?.store?.location_id ||
        this.getCurrentLocationId()
      );
    },

    async getProductAd() {
      try {
        this.advertisements = [];

        const location = this.getProductAdLocationId();

        const params = {
          type: 221,
        };

        if (location) {
          params.location = location;
        }

        const response = await api.get('commercials/advertisements/', {
          params,
        });

        this.advertisements = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    async getProductDetail() {
      try {
        const response = await api.get(`mall/products/${this.currentId}/`);
        this.detail = response.data;

        this.currentImage = this.detail?.images?.[0]?.path || this.detail?.thumbnail || null;

        this.isLiked = this.detail?.is_liked;
        this.variableProduct = this.detail?.stocks?.length > 0;

        this.formRate.rating = this.detail?.user_rating?.rating;
        this.userRating = this.detail?.user_rating?.rating || 0;

        if (this.variableProduct) {
          this.selectStock(this.detail.stocks[0]);
        } else {
          this.productPrice = this.detail?.price;
          this.productIsNew = this.detail?.is_new;
          this.productIsAvailable = this.detail?.is_available;
          this.productForCredit = this.detail?.for_credit;
          this.productInPromotion = this.detail?.in_promotion;
          this.productDiscount = this.detail?.discount;
          this.productDiscountPercent = this.detail?.discount_percent;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async getProductComments() {
      try {
        const response = await api.get(`mall/products/${this.currentId}/comment/`);
        this.comments = response.data.results;
      } catch (e) {
        console.log(e);
      }
    },
    async getProductSimilars() {
      try {
        const response = await api.get(`mall/products/${this.currentId}/more_like_this/`);
        this.similarProducts = response.data.results;
      } catch (e) {
        console.log(e);
      }
    },
    async addToCart() {
      this.loading = true;
      try {
        let body = {
          product: this.detail.id,
          quantity: 1,
          session: this.getOrderSessionId,
        };
        if (this.variableProduct) {
          body['size'] = this.selectedStock.size.id;
        }
        const response = await api.post('mall/cart-items/', body);
        this.$toast.info(`${i18n.global.t('warning.CART_ADDED')}`);
        await this.$store.getSessionCart();
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    deleteCartItem(qty) {
      this.$store.deleteCartItem(this.cartItem.id, qty).then(e => {
        this.loading = false;
      });
    },
    updateCartItem(qty) {
      this.loading = true;
      if (qty === 0) {
        this.deleteCartItem(this.cartItem.id, qty);
        return;
      }
      this.$store.updateCartItem(this.cartItem.id, qty).then(e => {
        this.loading = false;
      });
    },
    setUserRate(value) {
      this.userRating = value;
    },

    async postCommentVote(id, value) {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        const response = await api.post(`comments/${id}/vote/`, { vote: value });
        await this.getProductComments();
      } catch (e) {
        console.log(e);
      }
    },
  },
  computed: {
    ...mapState(useAppStore, ['getToken', 'getOrderSessionId', 'getCartItemById']),

    sellerFirstPhone() {
      const phones = this.detail?.store?.phone_numbers;

      if (Array.isArray(phones) && phones.length > 0) {
        return phones[0];
      }

      return this.detail?.store?.phone || null;
    },

    cartItem() {
      this.triggerComputed;
      if (this.selectedStock) {
        return this.getCartItemById(this.detail?.id, this.selectedStock?.size?.name);
      } else {
        return this.getCartItemById(this.detail?.id, null);
      }
    },
  },
  async created() {
    window.scrollTo(0, 0);
    this.currentId = this.$route.params.id;
    await this.getProductDetail();
    await this.getProductAd();
    await this.getProductSimilars();
    await this.getProductComments();
  },
};
</script>
